import { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { BUSINESS } from '../config/business';
import BusinessLegalStrip from './BusinessLegalStrip';
import { isNativeApp } from '../utils/platform.js';
import '../pages/LegalPage.css';

const LEGAL_LINKS = [
  { path: '/terms', key: 'footerTerms' },
  { path: '/privacy', key: 'footerPrivacy' },
  { path: '/refund', key: 'footerRefund' },
  { path: '/cancellation', key: 'footerCancellation' },
  { path: '/contact', key: 'contact' },
];

export default function LegalPageShell({
  title,
  children,
  onClose,
  standalone = false,
  teSummary = null,
  isContact = false,
}) {
  const { t } = useLang();
  const [legalLang, setLegalLang] = useState('en');
  const currentPath = typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') : '';
  const meta = LEGAL_PAGE_META[currentPath] || LEGAL_PAGE_META['/terms'];

  if (standalone) {
    return (
      <div className="legal-page">
        <header className="legal-page__header">
          <a href="/" className="legal-page__brand" aria-label={t.brandName}>
            <img src="/rk-logo.webp" alt={t.brandName} width={56} height={56} />
            <span className="legal-page__brand-text">{t.brandName}</span>
          </a>
          <button
            type="button"
            className="legal-page__home-link"
            onClick={() => window.location.replace('/')}
          >
            {t.nativeBackToApp || t.legalBackHome}
          </button>
        </header>
        {isNativeApp() ? (
          <div className="legal-page__native-bar">
            <button type="button" onClick={() => window.location.replace('/')}>
              {t.nativeBackToApp || t.legalBackHome}
            </button>
          </div>
        ) : null}

        <div className="legal-page__layout">
          <aside className="legal-page__aside" aria-label={t.legalPoliciesNav}>
            <p className="legal-page__aside-label">{t.legalPoliciesNav}</p>
            <nav className="legal-page__aside-nav">
              {LEGAL_LINKS.map((link) => (
                <a
                  key={link.path}
                  href={link.path}
                  className={currentPath === link.path ? 'is-active' : undefined}
                >
                  <span className="legal-page__aside-icon" aria-hidden>
                    {LEGAL_PAGE_META[link.path]?.icon}
                  </span>
                  {t[link.key]}
                </a>
              ))}
            </nav>
          </aside>

          <main className="legal-page__main">
            <div
              className={`legal-page__hero${isContact ? ' legal-page__hero--contact' : ''}`}
              style={{ '--legal-accent': meta.accent }}
            >
              <div className="legal-page__hero-icon" aria-hidden>
                {meta.icon}
              </div>
              <div className="legal-page__hero-copy">
                <h1 className="legal-page__title">{title}</h1>
                <p className="legal-page__hero-sub">
                  {isContact ? t.contactPageHeroSub : t.legalHeroSub}
                </p>
              </div>
              {teSummary && (
                <div className="legal-lang-toggle" role="tablist" aria-label="Policy language">
                  <button
                    type="button"
                    className={legalLang === 'en' ? 'is-active' : ''}
                    onClick={() => setLegalLang('en')}
                  >
                    {t.legalLangEn}
                  </button>
                  <button
                    type="button"
                    className={legalLang === 'te' ? 'is-active' : ''}
                    onClick={() => setLegalLang('te')}
                  >
                    {t.legalLangTe}
                  </button>
                </div>
              )}
            </div>

            <div
              className={`legal-body legal-body--standalone${isContact ? ' legal-body--contact' : ''}`}
              style={{ '--legal-accent': meta.accent }}
            >
              {!isContact && (
                <div className="legal-section legal-section--biz">
                  <BusinessLegalStrip />
                </div>
              )}
              {teSummary && legalLang === 'te' && (
                <div className="legal-section legal-section--te">
                  <h3>{teSummary.title}</h3>
                  <p>{teSummary.body}</p>
                  <p className="legal-te-note">{t.legalTeNote}</p>
                </div>
              )}
              {(legalLang === 'en' || !teSummary) && children}
            </div>
          </main>
        </div>

        <nav className="legal-page__nav-mobile" aria-label={t.legalPoliciesNav}>
          {LEGAL_LINKS.map((link) => (
            <a
              key={link.path}
              href={link.path}
              className={currentPath === link.path ? 'is-active' : undefined}
            >
              {t[link.key]}
            </a>
          ))}
        </nav>

        <footer className="legal-page__footer">
          <p>
            © {new Date().getFullYear()} {BUSINESS.legalName} · {t.brandName} · {BUSINESS.email}
          </p>
          <p className="legal-page__footer-gst">
            {t.gstLabel}: {BUSINESS.gstin}
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className="legal-backdrop" onClick={onClose} role="presentation">
      <div className="legal-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="legal-header">
          <h2>{title}</h2>
          <button type="button" className="legal-close" onClick={onClose} aria-label={t.closeBtn}>
            ✕
          </button>
        </div>
        <div className="legal-body">{children}</div>
      </div>
    </div>
  );
}
